import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  DOC, CURRENT_REVISION, REVISIONS, STANDARDS, LANES, SERVICE_LINES, TARGETS,
  NODES, PHASES, ISO_GAPS, OPEN_QUESTIONS,
  RMA_DOC, RMA_OPEN_QUESTIONS, RMA_OPEN_QUESTIONS_REV2,
  RMA_CURRENT_REVISION, RMA_REVISIONS, RMA_PHASES_BY_REV, RMA_DRIFT,
  LOOPS, HANDOFF, METRICS, NODE_METRICS, METRIC_FINDINGS,
} from './supportFlowData';

const POPOVER_WIDTH = 400;
const NARROW_BREAKPOINT = 980;

function parseRef(ref) {
  const idx = ref.indexOf(':');
  if (idx === -1) return { std: ref, clause: '' };
  return { std: ref.slice(0, idx), clause: ref.slice(idx + 1) };
}

function refLabel(ref) {
  const { std, clause } = parseRef(ref);
  const name = STANDARDS[std] ? STANDARDS[std].name : std;
  return clause ? `${name} ${clause}` : name;
}

function useIsNarrow(breakpoint) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

const TONE = {
  good: { color: 'var(--ni-green)', border: 'rgba(70,187,137,0.5)' },
  warn: { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)' },
  bad: { color: '#F09090', border: 'rgba(224,96,96,0.55)' },
  neutral: { color: 'var(--ni-text-muted)', border: 'var(--ni-border-soft)' },
};

/* ---------------------------------------------------------------- connectors */

const Down = ({ height = 24, tone = 'rgba(255,255,255,0.22)' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height, flex: 'none' }}>
    <div style={{ flex: 1, width: 0, borderLeft: `2px solid ${tone}` }} />
    <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `6px solid ${tone}` }} />
  </div>
);

const Right = ({ width = 40, tone = 'rgba(255,255,255,0.24)', dashed }) => (
  <div style={{ display: 'flex', alignItems: 'center', width, flex: 'none' }}>
    <div style={{ flex: 1, height: 0, borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${tone}` }} />
    <div style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: `7px solid ${tone}` }} />
  </div>
);

// Split / merge connectors for n columns. Column centres sit at (i + 0.5) / n.
const Split = ({ n = 2, merge }) => {
  const centres = Array.from({ length: n }, (_, i) => ((i + 0.5) / n) * 100);
  const first = centres[0];
  const last = centres[centres.length - 1];
  const line = 'rgba(255,255,255,0.22)';
  const head = 'rgba(255,255,255,0.34)';
  return (
    <div style={{ position: 'relative', height: 42, width: '100%', flex: 'none' }}>
      <div style={{ position: 'absolute', left: '50%', top: merge ? 28 : 0, height: 14, borderLeft: `2px solid ${line}` }} />
      <div style={{ position: 'absolute', left: `${first}%`, width: `${last - first}%`, top: 14, borderTop: `2px solid ${line}` }} />
      {centres.map(c => (
        <div key={c} style={{ position: 'absolute', left: `${c}%`, top: merge ? 0 : 14, height: 14, borderLeft: `2px solid ${line}` }} />
      ))}
      {merge
        ? <div style={{ position: 'absolute', left: 'calc(50% - 4px)', top: 36, width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `6px solid ${head}` }} />
        : centres.map(c => (
          <div key={`h${c}`} style={{ position: 'absolute', left: `calc(${c}% - 4px)`, top: 28, width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `6px solid ${head}` }} />
        ))}
    </div>
  );
};

const Pill = ({ children, tone }) => {
  const t = TONE[tone] || TONE.neutral;
  return (
    <span style={{
      fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
      color: t.color, border: `1px solid ${t.border}`, borderRadius: 999, padding: '2px 10px', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const FeedbackRail = ({ label = 'Quantified result to feed back' }) => (
  <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative', minWidth: 60 }}>
    <span style={{
      position: 'absolute', left: 10, top: -16, fontSize: 9, fontWeight: 700,
      letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ni-green)', whiteSpace: 'nowrap',
    }}>{label}</span>
    <div style={{ flex: 1, height: 0, borderTop: '2px solid var(--ni-green)' }} />
    <div style={{ width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '9px solid var(--ni-green)' }} />
  </div>
);

/* --------------------------------------------------------------------- node */

function NodeCard({ id, onSelect, selected, showIso, showMetrics, compact }) {
  const ref = useRef(null);
  const n = NODES[id];
  if (!n) return null;
  const lane = LANES[n.lane];
  const isDecision = n.kind === 'decision';
  const isEnd = n.kind === 'end';
  const isAnnotation = n.kind === 'annotation';
  const isStage = n.kind === 'stage';

  const base = {
    position: 'relative',
    background: lane.bg,
    borderWidth: selected ? 2 : 1,
    borderStyle: 'solid',
    borderColor: selected ? 'var(--ni-green)' : lane.border,
    borderRadius: 12,
    padding: compact || isAnnotation ? '8px 11px' : '10px 14px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    color: 'var(--ni-text)',
    font: 'inherit',
    boxShadow: selected ? '0 0 0 3px rgba(70,187,137,0.16), 0 8px 22px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.35)',
    transition: 'border-color 0.12s, box-shadow 0.12s, transform 0.12s',
    display: 'block',
  };
  if (isDecision) { base.borderRadius = 14; base.borderStyle = 'dashed'; base.borderWidth = 2; }
  if (isEnd) base.borderRadius = 999;
  if (isAnnotation) { base.borderStyle = 'dashed'; base.boxShadow = 'none'; }
  // A `stage` node is a real status in the RMA state machine — accented so the
  // system's own stages stand out from the actions that move between them.
  if (isStage) {
    base.borderLeftWidth = 4;
    base.borderLeftColor = lane.border;
    base.background = `linear-gradient(90deg, ${lane.bg}, rgba(0,0,0,0.25))`;
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onSelect(id, ref.current)}
      style={base}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      aria-expanded={selected}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.09em', color: lane.code }}>{n.code}</span>
        {isDecision && <span style={{ fontSize: 9, color: 'var(--ni-text-muted)', letterSpacing: '0.08em' }}>DECISION</span>}
        {isStage && <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', color: lane.code, border: `1px solid ${lane.border}`, borderRadius: 999, padding: '0 6px' }}>STATUS</span>}
        {isEnd && <span style={{ fontSize: 11, color: 'var(--ni-green)', fontWeight: 700 }}>✓</span>}
        {n.mttr && (
          <span style={{
            fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', color: '#C79BE0',
            border: '1px solid #A366C4', background: 'rgba(163,102,196,0.16)', borderRadius: 999, padding: '0 6px',
          }}>MTTR {n.mttr === 'start' ? 'START' : 'STOP'}</span>
        )}
        {n.proposed && (
          <span style={{
            fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', color: '#6FD3A6',
            border: '1px dashed rgba(70,187,137,0.6)', borderRadius: 999, padding: '0 6px',
          }}>PROPOSED</span>
        )}
        {n.lane === 'refurb' && (
          <span style={{
            fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', color: '#9AA1A3',
            border: '1px dashed #6E7476', borderRadius: 999, padding: '0 6px',
          }}>PLANNED</span>
        )}
        {n.detail && n.detail.flagNote && (
          <span style={{
            fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', color: '#D9B44A',
            border: '1px solid rgba(217,180,74,0.6)', borderRadius: 999, padding: '0 6px',
          }}>⚑ MARKED</span>
        )}
      </div>
      <div style={{ fontSize: compact || isAnnotation ? 12 : 13, fontWeight: 600, lineHeight: 1.3 }}>{n.title}</div>
      {showMetrics && NODE_METRICS[id] && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {NODE_METRICS[id].map(mid => {
            const m = METRICS[mid];
            if (!m) return null;
            const auto = m.capture === 'auto';
            return (
              <span key={mid} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 9, fontWeight: 800, letterSpacing: '0.04em',
                color: auto ? '#8ED4E8' : '#E8CD7E',
                background: auto ? 'rgba(63,167,196,0.14)' : 'rgba(217,180,74,0.13)',
                border: `1px ${auto ? 'solid' : 'dashed'} ${auto ? 'rgba(63,167,196,0.45)' : 'rgba(217,180,74,0.5)'}`,
                borderRadius: 4, padding: '1px 5px',
              }}>{auto ? '◆' : '✎'} {m.short}</span>
            );
          })}
        </div>
      )}
      {showIso && n.detail && n.detail.iso && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {n.detail.iso.map(r => (
            <span key={r.ref} style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', color: '#C7B2EC',
              background: 'rgba(169,139,219,0.14)', border: '1px solid rgba(169,139,219,0.35)',
              borderRadius: 4, padding: '1px 5px',
            }}>{refLabel(r.ref)}</span>
          ))}
        </div>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ popover */

function DetailPopover({ id, anchor, onClose }) {
  const n = NODES[id];
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 14;
    const width = Math.min(POPOVER_WIDTH, vw - 24);
    let left = r.right + gap;
    if (left + width > vw - 12) left = r.left - width - gap;
    if (left < 12) left = Math.max(12, Math.min(vw - width - 12, r.left));
    const top = Math.max(12, Math.min(r.top, vh - 300));
    setPos({ left, top, width, maxHeight: vh - top - 16 });
  }, [anchor]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [place]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    const onDown = e => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchor]);

  if (!n || !pos) return null;
  const lane = LANES[n.lane];
  const d = n.detail || {};

  const Section = ({ title, children }) => (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 5 }}>{title}</div>
      {children}
    </div>
  );

  const Bullets = ({ items }) => (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>
      {items.map((t, i) => <li key={i} style={{ marginBottom: 3 }}>{t}</li>)}
    </ul>
  );

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`${n.code} ${n.title}`}
      style={{
        position: 'fixed', left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight,
        overflowY: 'auto', zIndex: 9000,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.03), transparent), rgba(13,18,16,0.985)',
        border: `1px solid ${lane.border}`, borderRadius: 16,
        boxShadow: '0 26px 70px rgba(0,0,0,0.66)', padding: '14px 16px 18px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: lane.code }}>{n.code}</span>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: lane.code, background: lane.bg, border: `1px solid ${lane.border}`, borderRadius: 999, padding: '1px 8px',
            }}>{lane.label}</span>
            {n.proposed && (
              <span style={{
                fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: '#6FD3A6',
                border: '1px dashed rgba(70,187,137,0.6)', borderRadius: 999, padding: '1px 7px',
              }}>PROPOSED · REV 03</span>
            )}
            {n.mttr && (
              <span style={{
                fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: '#C79BE0',
                border: '1px solid #A366C4', borderRadius: 999, padding: '1px 7px',
              }}>MTTR {n.mttr === 'start' ? 'START' : 'STOP'}</span>
            )}
          </div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>{n.title}</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            flex: 'none', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--ni-border-soft)',
            color: 'var(--ni-text-soft)', borderRadius: 8, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '4px 8px',
          }}
        >✕</button>
      </div>

      {d.what && <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>{d.what}</p>}

      {d.why && (
        <div style={{
          marginTop: 12, borderLeft: '3px solid var(--ni-green)', background: 'rgba(70,187,137,0.07)',
          borderRadius: '0 10px 10px 0', padding: '9px 12px',
        }}>
          <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', color: 'var(--ni-green)', marginBottom: 4 }}>WHY THIS WAS ADDED</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{d.why}</div>
        </div>
      )}

      {n.items && (
        <Section title={n.kind === 'toolkit' ? 'Worked through in order' : 'Contents'}>
          <div style={{ display: 'grid', gap: 7 }}>
            {n.items.map((it, i) => (
              <div key={i} style={{ display: 'flex', gap: 8 }}>
                {it.code
                  ? <span style={{ fontSize: 9.5, fontWeight: 800, color: lane.code, minWidth: 38, paddingTop: 2 }}>{it.code}</span>
                  : <span style={{ color: lane.code, minWidth: 12, paddingTop: 1 }}>·</span>}
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{it.title}</div>
                  {it.note && <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--ni-text-muted)' }}>{it.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {d.owner && <Section title="Who owns this step"><div style={{ fontSize: 12.5, color: 'var(--ni-text-soft)' }}>{d.owner}</div></Section>}
      {d.api && (
        <Section title="In the system">
          <code style={{
            display: 'block', fontSize: 11.5, lineHeight: 1.55, color: '#8FBFE8', wordBreak: 'break-word',
            background: 'rgba(92,155,214,0.09)', border: '1px solid rgba(92,155,214,0.28)', borderRadius: 8, padding: '7px 10px',
          }}>{d.api}</code>
        </Section>
      )}
      {d.inputs && <Section title="Inputs"><Bullets items={d.inputs} /></Section>}
      {d.outputs && <Section title="Outputs"><Bullets items={d.outputs} /></Section>}
      {d.rules && <Section title="How it must be done"><Bullets items={d.rules} /></Section>}

      {d.watch && (
        <Section title="Watch out">
          <div style={{
            fontSize: 12.5, lineHeight: 1.55, color: '#E8CD7E',
            background: 'rgba(217,180,74,0.08)', border: '1px solid rgba(217,180,74,0.3)',
            borderRadius: 10, padding: '8px 11px',
          }}>{d.watch}</div>
        </Section>
      )}

      {d.flagNote && (
        <Section title="From the printed review">
          <div style={{ fontSize: 12.5, lineHeight: 1.55, color: '#D9B44A' }}>⚑ {d.flagNote}</div>
        </Section>
      )}

      {NODE_METRICS[id] && (
        <Section title="Feeds these measures">
          <div style={{ display: 'grid', gap: 6 }}>
            {NODE_METRICS[id].map(mid => {
              const m = METRICS[mid];
              if (!m) return null;
              const auto = m.capture === 'auto';
              return (
                <div key={mid} style={{
                  background: auto ? 'rgba(63,167,196,0.09)' : 'rgba(217,180,74,0.08)',
                  border: `1px ${auto ? 'solid' : 'dashed'} ${auto ? 'rgba(63,167,196,0.3)' : 'rgba(217,180,74,0.35)'}`,
                  borderRadius: 10, padding: '7px 10px',
                }}>
                  <div style={{ display: 'flex', gap: 7, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 800, color: auto ? '#8ED4E8' : '#E8CD7E' }}>{m.short}</span>
                    <span style={{ fontSize: 11, color: 'var(--ni-text-soft)' }}>{m.name}</span>
                    <span style={{
                      fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: auto ? '#8ED4E8' : '#E8CD7E', marginLeft: 'auto',
                    }}>{auto ? 'automatic' : 'manual'}</span>
                  </div>
                  <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--ni-text-muted)', marginTop: 3 }}>{m.how}</div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {d.iso && (
        <Section title="Standards this satisfies">
          <div style={{ display: 'grid', gap: 6 }}>
            {d.iso.map(r => (
              <div key={r.ref} style={{
                background: 'rgba(169,139,219,0.09)', border: '1px solid rgba(169,139,219,0.28)',
                borderRadius: 10, padding: '7px 10px',
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', color: '#C7B2EC' }}>{refLabel(r.ref)}</div>
                <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--ni-text-muted)', marginTop: 2 }}>{r.why}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------- bands */

// A node plus an optional dotted-connected annotation card (emails, planned work).
function NodeWithAnnotation({ id, annotate, ctx, width = 380, compact }) {
  const { onSelect, selected, showIso, showMetrics, narrow } = ctx;
  const props = nid => ({ id: nid, onSelect, selected: selected === nid, showIso, showMetrics });
  const annNode = annotate && NODES[annotate];
  if (!annNode) {
    return <div style={{ width: '100%', maxWidth: width }}><NodeCard {...props(id)} compact={compact} /></div>;
  }
  const tone = LANES[annNode.lane].border;
  return (
    <div style={{
      display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: narrow ? 'stretch' : 'center',
      gap: narrow ? 8 : 0, width: '100%', maxWidth: width + 300, justifyContent: 'center',
    }}>
      <div style={{ width: '100%', maxWidth: width }}><NodeCard {...props(id)} compact={compact} /></div>
      {!narrow && <Right width={34} tone={tone} dashed />}
      <div style={{ width: '100%', maxWidth: 250 }}><NodeCard {...props(annotate)} compact /></div>
    </div>
  );
}

function Band({ band, ctx }) {
  const { onSelect, selected, showIso, showMetrics, narrow, showFeedback } = ctx;
  const nodeProps = id => ({ id, onSelect, selected: selected === id, showIso, showMetrics });

  switch (band.layout) {
    case 'single':
      return (
        <>
          <NodeWithAnnotation id={band.node} annotate={band.annotate} ctx={ctx} width={band.target ? 460 : 380} />
          {band.target && showFeedback && (
            <div style={{ width: '100%', maxWidth: 460, marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--ni-green)', textTransform: 'uppercase' }}>Target sits here</span>
              <div style={{ flex: 1, height: 0, borderTop: '2px dashed rgba(70,187,137,0.6)' }} />
            </div>
          )}
          <Down />
        </>
      );

    case 'merge':
      return (
        <>
          <div style={{ width: '100%', maxWidth: 900 }}><Split n={2} merge /></div>
          <NodeWithAnnotation id={band.node} annotate={band.annotate} ctx={ctx} />
          <Down />
        </>
      );

    case 'chain':
      return (
        <>
          <div style={{
            display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: narrow ? 'stretch' : 'center',
            width: '100%', maxWidth: 940, justifyContent: 'center',
          }}>
            {band.nodes.map((id, i) => (
              <React.Fragment key={id}>
                {i > 0 && (narrow ? <Down height={20} /> : <Right />)}
                <div style={{ flex: 1, minWidth: 0, maxWidth: 420 }}><NodeCard {...nodeProps(id)} /></div>
              </React.Fragment>
            ))}
          </div>
          <Down />
        </>
      );

    case 'pairGrid':
      return (
        <>
          <div style={{ width: '100%' }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ni-text-muted)', textAlign: 'center', marginBottom: 12,
            }}>{band.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))', gap: 12 }}>
              {band.pairs.map(p => (
                <div key={p.channel} style={{ display: 'flex', flexDirection: 'column' }}>
                  <NodeCard {...nodeProps(p.channel)} compact />
                  <div style={{ display: 'flex', justifyContent: 'center' }}><Down height={16} /></div>
                  <NodeCard {...nodeProps(p.capture)} compact />
                </div>
              ))}
            </div>
          </div>
          <Down height={30} />
        </>
      );

    case 'toolkit': {
      const n = NODES[band.node];
      const lane = LANES[n.lane];
      return (
        <>
          <button
            type="button"
            onClick={e => onSelect(band.node, e.currentTarget)}
            style={{
              width: '100%', maxWidth: 940, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--ni-text)',
              background: lane.bg, borderWidth: selected === band.node ? 2 : 1, borderStyle: 'solid',
              borderColor: selected === band.node ? 'var(--ni-green)' : lane.border,
              borderRadius: 16, padding: '14px 18px', boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', color: lane.code, textTransform: 'uppercase' }}>
                {n.title} — {n.code}
              </span>
              <span style={{ fontSize: 10, color: 'var(--ni-text-muted)', letterSpacing: '0.08em' }}>WORKED IN ORDER · CLICK FOR DETAIL</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '7px 26px' }}>
              {n.items.map(it => (
                <div key={it.code + it.title} style={{ display: 'flex', gap: 8, fontSize: 12.5, lineHeight: 1.4 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: lane.code, minWidth: 40, flex: 'none' }}>{it.code}</span>
                  <span>{it.title}</span>
                </div>
              ))}
            </div>
          </button>
          <Down />
        </>
      );
    }

    case 'sidecar': {
      const left = band.side === 'left';
      const main = <div style={{ width: '100%', maxWidth: 400 }}><NodeCard {...nodeProps(band.node)} /></div>;
      const sideCol = (
        <div style={{ width: '100%', maxWidth: 280 }}>
          <Pill tone="bad">{band.sideLabel}</Pill>
          {band.sideNodes.map((id, i) => (
            <React.Fragment key={id}>
              {i > 0 && <div style={{ display: 'flex', justifyContent: 'center' }}><Down height={18} /></div>}
              <div style={{ marginTop: i === 0 ? 8 : 0 }}><NodeCard {...nodeProps(id)} compact /></div>
            </React.Fragment>
          ))}
          {band.loopLabel && (
            <div style={{ marginTop: 8, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--ni-text-muted)', textTransform: 'uppercase' }}>
              ↺ {band.loopLabel} → {NODES[band.node].code}
            </div>
          )}
        </div>
      );
      return (
        <>
          <div style={{
            display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: narrow ? 'stretch' : 'center',
            gap: narrow ? 14 : 0, width: '100%', maxWidth: 940, justifyContent: 'center',
          }}>
            {!narrow && left && <>{sideCol}<Right width={44} /></>}
            {main}
            {!narrow && !left && <><Right width={44} />{sideCol}</>}
            {narrow && sideCol}
          </div>
          {band.passLabel && <div style={{ marginTop: 8 }}><Pill tone="good">{band.passLabel}</Pill></div>}
          <Down />
        </>
      );
    }

    // `branch` renders two columns; `fan` renders any number via `cols`.
    case 'branch':
    case 'fan': {
      const cols = band.cols || [band.left, band.right];
      const col = side => (
        <div key={side.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Pill tone={side.tone}>{side.label}</Pill>
          <div style={{ height: 10 }} />
          {side.nodes.map((id, i) => (
            <React.Fragment key={id}>
              {i > 0 && <Down height={20} />}
              {side.annotate && side.annotate[id]
                ? <div style={{ width: '100%' }}><NodeWithAnnotation id={id} annotate={side.annotate[id]} ctx={ctx} width={300} compact /></div>
                : <div style={{ width: '100%', maxWidth: 320 }}><NodeCard {...nodeProps(id)} /></div>}
            </React.Fragment>
          ))}
          {side.footer && (
            <div style={{ marginTop: 10, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--ni-text-muted)', textTransform: 'uppercase', textAlign: 'center' }}>
              {side.footer}
            </div>
          )}
        </div>
      );
      return (
        <>
          {!narrow && <div style={{ width: '100%', maxWidth: 900 }}><Split n={cols.length} /></div>}
          {narrow && <Down height={20} />}
          <div style={{
            display: 'grid', gridTemplateColumns: narrow ? '1fr' : `repeat(${cols.length}, 1fr)`,
            gap: narrow ? 26 : '0 28px', width: '100%', maxWidth: 900,
          }}>
            {cols.map(col)}
          </div>
        </>
      );
    }

    case 'close':
      return (
        <div style={{
          display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: narrow ? 'stretch' : 'center',
          gap: 12, width: '100%', marginTop: 18,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'var(--ni-text-muted)', border: '1px solid var(--ni-border-soft)', borderRadius: 999,
            padding: '2px 9px', whiteSpace: 'nowrap', alignSelf: narrow ? 'flex-start' : 'center',
          }}>{band.from}</span>
          <div style={{ width: '100%', maxWidth: 340 }}><NodeCard {...nodeProps(band.node)} /></div>
          {band.loopTo && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#E8CD7E', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              ↳ {band.loopTo}
            </span>
          )}
          {!band.loopTo && showFeedback && !narrow && <FeedbackRail />}
        </div>
      );

    case 'chainClose':
      return (
        <div style={{
          display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: narrow ? 'stretch' : 'center',
          gap: narrow ? 10 : 0, width: '100%', marginTop: 18,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'var(--ni-text-muted)', border: '1px solid var(--ni-border-soft)', borderRadius: 999,
            padding: '2px 9px', whiteSpace: 'nowrap', marginRight: narrow ? 0 : 12, alignSelf: narrow ? 'flex-start' : 'center',
          }}>{band.from}</span>
          {band.nodes.map((id, i) => (
            <React.Fragment key={id}>
              {i > 0 && (narrow ? <Down height={18} /> : <Right width={34} />)}
              <div style={{ flex: 1, minWidth: 0, maxWidth: 300 }}><NodeCard {...nodeProps(id)} compact /></div>
            </React.Fragment>
          ))}
          {showFeedback && !narrow && <FeedbackRail label="Quantified result" />}
        </div>
      );

    case 'hub':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 16, width: '100%', maxWidth: 940 }}>
          {band.nodes.map(id => <div key={id}><NodeCard {...nodeProps(id)} /></div>)}
        </div>
      );

    default:
      return null;
  }
}

/* ------------------------------------------------------------ flow renderer */

function PhaseStack({ phases, ctx }) {
  return phases.map(phase => (
    <section key={phase.id} style={{ marginTop: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <span style={{
          width: 26, height: 26, flex: 'none', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12.5, fontWeight: 800, color: 'var(--ni-green)',
          background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.4)',
        }}>{phase.n}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase' }}>{phase.title}</span>
        <span style={{ fontSize: 11.5, color: 'var(--ni-text-muted)' }}>{phase.subtitle}</span>
        {phase.from && (
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'var(--ni-text-muted)', border: '1px solid var(--ni-border-soft)', borderRadius: 999, padding: '2px 9px',
          }}>from {phase.from}</span>
        )}
        <div style={{ flex: 1, height: 1, background: 'var(--ni-border-soft)', minWidth: 24 }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {phase.bands.map((band, i) => <Band key={`${phase.id}-${i}`} band={band} ctx={ctx} />)}
      </div>
    </section>
  ));
}

// The interface between the two loops, drawn as a bridge rather than a gap.
function Handoff({ narrow }) {
  const card = (side, tone) => (
    <div style={{
      border: `1px solid ${tone.border}`, background: tone.bg, borderRadius: 16, padding: '14px 17px',
    }}>
      <div style={{ display: 'flex', gap: 9, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 7 }}>
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: tone.color }}>
          {side.title}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 9 }}>
        <code style={{ fontSize: 11, fontWeight: 700, color: 'var(--ni-text)', background: 'rgba(255,255,255,0.06)', borderRadius: 6, padding: '2px 7px' }}>{side.from}</code>
        <span style={{ color: tone.color, fontSize: 14 }}>→</span>
        <code style={{ fontSize: 11, fontWeight: 700, color: 'var(--ni-text)', background: 'rgba(255,255,255,0.06)', borderRadius: 6, padding: '2px 7px' }}>{side.to}</code>
      </div>
      <p style={{ margin: '0 0 9px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>{side.lead}</p>
      <ol style={{ margin: '0 0 9px', paddingLeft: 17, fontSize: 12, lineHeight: 1.6, color: 'var(--ni-text-muted)' }}>
        {side.steps.map((t, i) => <li key={i} style={{ marginBottom: 3 }}>{t}</li>)}
      </ol>
      <div style={{ fontSize: 11.5, lineHeight: 1.55, color: tone.color, borderTop: `1px solid ${tone.border}`, paddingTop: 8 }}>{side.note}</div>
    </div>
  );

  return (
    <div style={{
      border: '1px solid rgba(142,124,195,0.45)', borderRadius: 18,
      background: 'linear-gradient(180deg, rgba(142,124,195,0.08), rgba(70,187,137,0.05))',
      padding: 'clamp(0.9rem, 2vw, 1.6rem)',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#BCA9E0',
          border: '1px solid rgba(142,124,195,0.5)', borderRadius: 999, padding: '2px 9px',
        }}>The seam</span>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Where the support loop becomes the service loop</h2>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.65, color: 'var(--ni-text-muted)', maxWidth: 900 }}>
        One process, two systems. The <strong style={{ color: 'var(--ni-text)' }}>{LOOPS.support.name}</strong> runs in{' '}
        {LOOPS.support.system}; the <strong style={{ color: 'var(--ni-text)' }}>{LOOPS.service.name}</strong> runs in{' '}
        {LOOPS.service.system}. They are joined by one field — <code style={{ fontSize: 12 }}>odooTicketId</code> — and by the
        quantified result that comes back at the end. Both directions below are implemented, not proposed.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 14 }}>
        {card(HANDOFF.down, { color: '#BCA9E0', border: 'rgba(142,124,195,0.4)', bg: 'rgba(142,124,195,0.07)' })}
        {card(HANDOFF.up, { color: 'var(--ni-green)', border: 'rgba(70,187,137,0.4)', bg: 'rgba(70,187,137,0.06)' })}
      </div>
    </div>
  );
}

const flowFrame = {
  border: '1px solid var(--ni-border-soft)',
  borderRadius: 18,
  background: 'rgba(17,22,20,0.55)',
  padding: 'clamp(0.9rem, 2vw, 1.6rem)',
};

/* ---------------------------------------------------------------- main page */

export default function SupportFlowPage() {
  const navigate = useNavigate();
  const narrow = useIsNarrow(NARROW_BREAKPOINT);
  const [revision, setRevision] = useState(CURRENT_REVISION);
  const [rmaRevision, setRmaRevision] = useState(RMA_CURRENT_REVISION);
  const [view, setView] = useState('diagram');
  const [showProposed, setShowProposed] = useState(true);
  const [showIso, setShowIso] = useState(false);
  const [showMetrics, setShowMetrics] = useState(true);
  const [showLayers, setShowLayers] = useState(false);
  const [sel, setSel] = useState(null);

  const rev = useMemo(() => REVISIONS.find(r => r.rev === revision) || REVISIONS[REVISIONS.length - 1], [revision]);
  const rmaRev = useMemo(
    () => RMA_REVISIONS.find(r => r.rev === rmaRevision) || RMA_REVISIONS[RMA_REVISIONS.length - 1],
    [rmaRevision]
  );
  const rmaPhases = RMA_PHASES_BY_REV[rmaRevision] || RMA_PHASES_BY_REV[RMA_CURRENT_REVISION];
  const showFeedback = revision >= 2;

  const onSelect = useCallback((id, el) => {
    setSel(prev => (prev && prev.id === id ? null : { id, el }));
  }, []);

  const nodeVisible = useCallback(id => {
    const n = NODES[id];
    if (!n) return false;
    if (n.since && n.since > revision) return false;
    if (n.proposed && !showProposed) return false;
    return true;
  }, [revision, showProposed]);

  const visiblePhases = useMemo(() => PHASES.map(phase => {
    const bands = phase.bands.filter(band => {
      if (band.since && band.since > revision) return false;
      if (band.layout === 'pairGrid') return true;
      const ids = band.node ? [band.node] : [];
      if (band.nodes) ids.push(...band.nodes);
      if (band.sideNodes) ids.push(...band.sideNodes);
      if (band.left) ids.push(...band.left.nodes);
      if (band.right) ids.push(...band.right.nodes);
      const known = ids.filter(id => NODES[id]);
      if (known.length === 0) return true;
      return known.some(nodeVisible);
    }).map(band => {
      if (band.layout === 'sidecar' && band.sideNodes) return { ...band, sideNodes: band.sideNodes.filter(nodeVisible) };
      if (band.layout === 'chain' || band.layout === 'chainClose' || band.layout === 'hub') {
        return { ...band, nodes: band.nodes.filter(nodeVisible) };
      }
      return band;
    });
    return { ...phase, bands, hidden: (phase.since && phase.since > revision) || bands.length === 0 };
  }).filter(p => !p.hidden), [revision, nodeVisible]);

  useEffect(() => { if (sel && !nodeVisible(sel.id)) setSel(null); }, [sel, nodeVisible]);
  useEffect(() => { setSel(null); }, [view]);

  const ctx = { onSelect, selected: sel && sel.id, showIso, showMetrics, narrow, showFeedback };

  const toneForStatus = st => (st.startsWith('Draft')
    ? { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.1)' }
    : st === 'Issued' || st === 'As built'
      ? { color: 'var(--ni-green)', border: 'rgba(70,187,137,0.5)', bg: 'rgba(70,187,137,0.1)' }
      : { color: 'var(--ni-text-muted)', border: 'var(--ni-border-soft)', bg: 'rgba(255,255,255,0.03)' });
  const rmaStatusTone = toneForStatus(rmaRev.status);
  const statusTone = rev.status.startsWith('Draft')
    ? { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.1)' }
    : rev.status === 'Issued'
      ? { color: 'var(--ni-green)', border: 'rgba(70,187,137,0.5)', bg: 'rgba(70,187,137,0.1)' }
      : { color: 'var(--ni-text-muted)', border: 'var(--ni-border-soft)', bg: 'rgba(255,255,255,0.03)' };

  return (
    <div className="ni-page">
      <div className="ni-shell">
        <header className="ni-header">
          <div className="ni-brand">
            <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Support Tools</span>
              <span className="ni-brand-title">Support loop &amp; RMA intake</span>
              <span className="ni-brand-subtitle">
                {LOOPS.support.name} ({LOOPS.support.system}) · {rev.label} &nbsp;→&nbsp; {LOOPS.service.name} ({LOOPS.service.system}) · {rmaRev.label}
              </span>
            </div>
          </div>
          <div className="ni-toolbar">
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/support')}>← Support Tools</button>
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/')}>Main</button>
          </div>
        </header>

        {/* Sticky control bar — the only chrome on the diagram view */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 60,
          background: 'rgba(11,15,13,0.94)', backdropFilter: 'blur(8px)',
          borderTop: '1px solid var(--ni-border-soft)', borderBottom: '1px solid var(--ni-border-soft)',
          padding: '8px 4px', margin: '0 -4px',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', alignItems: 'center' }}>
            <RevPicker
              flow="A"
              revisions={REVISIONS}
              current={revision}
              onPick={setRevision}
              status={rev.status}
              statusTone={statusTone}
            />
            <RevPicker
              flow="B"
              revisions={RMA_REVISIONS}
              current={rmaRevision}
              onPick={setRmaRevision}
              status={rmaRev.status}
              statusTone={rmaStatusTone}
            />

            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {[['diagram', 'Flow'], ['iso', 'ISO'], ['revisions', 'Revisions'], ['notes', 'Notes']].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setView(k)}
                  style={{
                    font: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 999, padding: '4px 11px',
                    border: `1px solid ${view === k ? 'var(--ni-border-strong)' : 'transparent'}`,
                    background: view === k ? 'rgba(255,255,255,0.07)' : 'transparent',
                    color: view === k ? 'var(--ni-text)' : 'var(--ni-text-muted)',
                  }}
                >{label}</button>
              ))}
            </div>

            {view === 'diagram' && (
              <button
                type="button"
                onClick={() => setShowLayers(v => !v)}
                style={{
                  font: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 999, padding: '4px 11px',
                  marginLeft: 'auto', border: '1px solid var(--ni-border-soft)',
                  background: showLayers ? 'rgba(255,255,255,0.07)' : 'transparent', color: 'var(--ni-text-muted)',
                }}
              >{showLayers ? '▴' : '▾'} Layers &amp; legend</button>
            )}
          </div>

          {view === 'diagram' && showLayers && (
            <div style={{ marginTop: 9, paddingTop: 9, borderTop: '1px solid var(--ni-border-soft)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 9 }}>
                <Toggle on={showProposed} onClick={() => setShowProposed(v => !v)} disabled={revision < 3}>Rev 03 proposals</Toggle>
                <Toggle on={showIso} onClick={() => setShowIso(v => !v)}>ISO clause tags</Toggle>
                <Toggle on={showMetrics} onClick={() => setShowMetrics(v => !v)}>Metric capture points</Toggle>
                <span style={{ fontSize: 11.5, color: 'var(--ni-text-muted)' }}>Click any block for owner, inputs, outputs and rules.</span>
              </div>
              {showMetrics && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: 9, fontSize: 10.5, color: 'var(--ni-text-muted)' }}>
                  <span style={{ color: '#8ED4E8' }}>◆ captured automatically</span>
                  <span style={{ color: '#E8CD7E' }}>✎ someone has to enter it</span>
                  <span>— click a block to see which measure it feeds</span>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px' }}>
                {Object.entries(LANES).map(([key, lane]) => (
                  <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, letterSpacing: '0.05em', color: 'var(--ni-text-muted)', textTransform: 'uppercase' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: lane.bg, border: `1.5px solid ${lane.border}`, flex: 'none' }} />
                    {lane.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {view === 'diagram' && (
          <>
            <div style={flowFrame}>
              <FlowTitle
                eyebrow={`Flow A · ${LOOPS.support.name}`}
                system={LOOPS.support.system}
                title={DOC.title}
                meta={`${DOC.id} · ${rev.label} · ${rev.date}`}
                note={`${LOOPS.support.scope} Ends at #5.2, where the unit is coming back — that cut-off is the seam below.`}
              />
              <PhaseStack phases={visiblePhases} ctx={ctx} />
            </div>

            <Handoff narrow={narrow} />

            <div style={flowFrame}>
              <FlowTitle
                eyebrow={`Flow B · ${LOOPS.service.name}`}
                system={LOOPS.service.system}
                title={RMA_DOC.title}
                meta={`${RMA_DOC.id} · ${rmaRev.label} · ${rmaRev.date}`}
                note={rmaRevision === 2
                  ? `${LOOPS.service.scope} Read from the implementation — codes are the real statuses and endpoints. MTTR runs from the first pending/assigned entry to the last shipped/archived entry, so inbound transit counts.`
                  : `${LOOPS.service.scope} This is the flowchart as drawn, kept for comparison. Switch B to Rev 02 for what the system actually does.`}
              />
              <PhaseStack phases={rmaPhases} ctx={ctx} />
            </div>
          </>
        )}

        {view === 'iso' && <IsoView narrow={narrow} />}
        {view === 'revisions' && <RevisionsView />}
        {view === 'notes' && <NotesView narrow={narrow} />}
      </div>

      {sel && nodeVisible(sel.id) && <DetailPopover id={sel.id} anchor={sel.el} onClose={() => setSel(null)} />}
    </div>
  );
}

function RevPicker({ flow, revisions, current, onPick, status, statusTone }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <span style={{
        fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: 'var(--ni-green)',
        border: '1px solid rgba(70,187,137,0.4)', borderRadius: 6, padding: '1px 5px',
      }}>{flow}</span>
      {revisions.map(r => (
        <button
          key={r.rev}
          type="button"
          onClick={() => onPick(r.rev)}
          title={r.summary}
          style={{
            font: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 999, padding: '4px 9px',
            border: `1px solid ${r.rev === current ? 'rgba(70,187,137,0.55)' : 'var(--ni-border-soft)'}`,
            background: r.rev === current ? 'rgba(70,187,137,0.14)' : 'transparent',
            color: r.rev === current ? 'var(--ni-green)' : 'var(--ni-text-muted)',
          }}
        >{r.label.replace('Rev ', '')}</button>
      ))}
      <span style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: statusTone.color, border: `1px solid ${statusTone.border}`, background: statusTone.bg,
        borderRadius: 999, padding: '2px 7px', whiteSpace: 'nowrap',
      }}>{status}</span>
    </div>
  );
}

function FlowTitle({ eyebrow, title, meta, note, system }) {
  return (
    <div style={{ borderBottom: '1px solid var(--ni-border-soft)', paddingBottom: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ni-green)',
          border: '1px solid rgba(70,187,137,0.45)', borderRadius: 999, padding: '2px 9px',
        }}>{eyebrow}</span>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h2>
        {system && (
          <span style={{
            fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#BCA9E0',
            border: '1px solid rgba(142,124,195,0.45)', background: 'rgba(142,124,195,0.1)',
            borderRadius: 999, padding: '2px 9px',
          }}>runs in {system}</span>
        )}
        <span style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginLeft: 'auto' }}>{meta}</span>
      </div>
      {note && <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.55, color: 'var(--ni-text-muted)' }}>{note}</p>}
    </div>
  );
}

function Toggle({ on, onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, cursor: disabled ? 'not-allowed' : 'pointer',
        font: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: '0.03em',
        padding: '4px 11px', borderRadius: 999, opacity: disabled ? 0.4 : 1,
        border: `1px solid ${on ? 'rgba(70,187,137,0.5)' : 'var(--ni-border-soft)'}`,
        background: on ? 'rgba(70,187,137,0.13)' : 'rgba(255,255,255,0.02)',
        color: on ? 'var(--ni-green)' : 'var(--ni-text-muted)',
      }}
    >
      <span style={{
        width: 8, height: 8, borderRadius: '50%', flex: 'none',
        background: on ? 'var(--ni-green)' : 'transparent',
        border: `1px solid ${on ? 'var(--ni-green)' : 'var(--ni-border-strong)'}`,
      }} />
      {children}
    </button>
  );
}

/* -------------------------------------------------------------- notes view */

function NotesView({ narrow }) {
  const questionCard = q => (
    <div key={q.id} style={{
      border: '1px dashed rgba(217,180,74,0.55)', background: 'rgba(217,180,74,0.06)',
      borderRadius: 12, padding: '11px 14px',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#E8CD7E', marginBottom: 3 }}>⚠ {q.question}</div>
      <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{q.detail}</div>
    </div>
  );

  return (
    <>
      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Targets the flow serves</h2>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginTop: 14 }}>
          {TARGETS.map(t => (
            <div key={t.metric} style={{
              border: '1px solid rgba(70,187,137,0.4)', background: 'rgba(70,187,137,0.06)',
              borderRadius: 14, padding: '13px 16px',
            }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--ni-green)', lineHeight: 1 }}>{t.metric}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 6 }}>{t.label}</div>
              <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--ni-text-muted)', marginTop: 5 }}>{t.detail}</div>
              {t.unconfirmed && (
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', color: '#E8CD7E', marginTop: 7, textTransform: 'uppercase' }}>
                  ⚠ Confirm this number
                </div>
              )}
            </div>
          ))}
          <div style={{ border: '1px solid var(--ni-border-soft)', background: 'rgba(255,255,255,0.02)', borderRadius: 14, padding: '13px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 8 }}>
              Service line (from the review)
            </div>
            {SERVICE_LINES.map(l => (
              <div key={l.tier} style={{ display: 'flex', gap: 9, marginBottom: 7 }}>
                <span style={{
                  fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', color: LANES[l.lane].code,
                  background: LANES[l.lane].bg, border: `1px solid ${LANES[l.lane].border}`,
                  borderRadius: 999, padding: '1px 8px', flex: 'none', height: 18,
                }}>{l.tier}</span>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{l.owner}</div>
                  <div style={{ fontSize: 11, lineHeight: 1.45, color: 'var(--ni-text-muted)' }}>{l.note}</div>
                </div>
              </div>
            ))}
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', color: '#E8CD7E', marginTop: 4, textTransform: 'uppercase' }}>
              ⚠ Mapping inferred — confirm
            </div>
          </div>
        </div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Open questions — Flow A, support loop</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Read from the handwritten comments on the printed review. Each is a reading, not a confirmed fact — settle these before the flow is issued.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>{OPEN_QUESTIONS.map(questionCard)}</div>
        <p className="ni-note" style={{ marginBottom: 0, marginTop: 14 }}>Source: {DOC.source}</p>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>What the process measures on its own</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Nine of these are produced by the database as a side effect of working the case — nobody compiles them, and
          nobody can forget them. Two need a person. Turn on <strong>Metric capture points</strong> on the diagram to see
          where each one is taken.
        </p>
        <div style={{ display: 'grid', gap: 11 }}>
          {Object.values(METRICS).map(m => {
            const auto = m.capture === 'auto';
            const t = auto
              ? { color: '#8ED4E8', border: 'rgba(63,167,196,0.4)', bg: 'rgba(63,167,196,0.07)' }
              : { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.06)' };
            return (
              <div key={m.id} style={{ border: `1px ${auto ? 'solid' : 'dashed'} ${t.border}`, background: t.bg, borderRadius: 14, padding: '13px 16px' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 7 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: t.color }}>{m.short}</span>
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>{m.name}</span>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: t.color, border: `1px solid ${t.border}`, borderRadius: 999, padding: '2px 9px', marginLeft: 'auto',
                  }}>{auto ? '◆ automatic' : '✎ manual'}</span>
                </div>
                <p style={{ margin: '0 0 8px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>{m.what}</p>
                <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '7px 20px' }}>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 3 }}>How it is produced</div>
                    <div style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{m.how}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 3 }}>Where you see it</div>
                    <div style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{m.surfaced}</div>
                  </div>
                </div>
                {m.watch && (
                  <div style={{ marginTop: 9, paddingTop: 8, borderTop: `1px solid ${t.border}`, fontSize: 12, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>
                    <strong style={{ color: t.color }}>Note · </strong>{m.watch}
                  </div>
                )}
                <code style={{ display: 'block', marginTop: 6, fontSize: 11, color: '#8FBFE8' }}>{m.where}</code>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Measurement findings — things the code does that nobody chose</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Found by reading the implementation rather than by asking. One is a defect, two are definitions worth stating
          out loud, and the rest are blind spots.
        </p>
        <div style={{ display: 'grid', gap: 11 }}>
          {METRIC_FINDINGS.map(f => {
            const t = f.severity === 'high'
              ? { color: '#F09090', border: 'rgba(224,96,96,0.55)', bg: 'rgba(224,96,96,0.07)' }
              : f.severity === 'medium'
                ? { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.06)' }
                : { color: 'var(--ni-text-muted)', border: 'var(--ni-border-soft)', bg: 'rgba(255,255,255,0.02)' };
            return (
              <div key={f.id} style={{ border: `1px solid ${t.border}`, background: t.bg, borderRadius: 14, padding: '13px 16px' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 7 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: t.color, border: `1px solid ${t.border}`, borderRadius: 999, padding: '2px 9px',
                  }}>{f.kind}</span>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{f.title}</span>
                </div>
                <p style={{ margin: '0 0 7px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>{f.detail}</p>
                <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>
                  <strong style={{ color: t.color }}>Effect · </strong>{f.effect}
                </div>
                <div style={{ marginTop: 7, paddingTop: 8, borderTop: `1px solid ${t.border}`, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>
                  <strong style={{ color: 'var(--ni-green)' }}>What it would take · </strong>{f.fix}
                </div>
                <code style={{ display: 'block', marginTop: 6, fontSize: 11, color: '#8FBFE8' }}>{f.where}</code>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Drawn vs built — where Flow B Rev 01 and the system disagree</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Ten differences between the flowchart and the running RMA system, read from the code. Rev 02 of Flow B is built
          from the right-hand column. The <em>where</em> line names the file, so every claim can be checked.
        </p>
        <div style={{ display: 'grid', gap: 11 }}>
          {RMA_DRIFT.map(d => {
            const t = d.severity === 'high'
              ? { color: '#F09090', border: 'rgba(224,96,96,0.55)', bg: 'rgba(224,96,96,0.07)', label: 'Matters' }
              : d.severity === 'medium'
                ? { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.06)', label: 'Worth knowing' }
                : { color: 'var(--ni-text-muted)', border: 'var(--ni-border-soft)', bg: 'rgba(255,255,255,0.02)', label: 'Detail' };
            return (
              <div key={d.id} style={{ border: `1px solid ${t.border}`, background: t.bg, borderRadius: 14, padding: '13px 16px' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: t.color, border: `1px solid ${t.border}`, borderRadius: 999, padding: '2px 9px',
                  }}>{t.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{d.topic}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '8px 20px' }}>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 3 }}>As drawn</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{d.drawn}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-green)', marginBottom: 3 }}>As built</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{d.built}</div>
                  </div>
                </div>
                <div style={{ marginTop: 9, paddingTop: 8, borderTop: `1px solid ${t.border}`, fontSize: 12, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>
                  <strong style={{ color: t.color }}>So what · </strong>{d.soWhat}
                </div>
                <code style={{ display: 'block', marginTop: 6, fontSize: 11, color: '#8FBFE8' }}>{d.where}</code>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Still genuinely open — Flow B</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Only the questions the code cannot answer. Everything about measurement moved to the two sections above, because
          the system already decides it.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>{RMA_OPEN_QUESTIONS_REV2.map(questionCard)}</div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Open questions — Flow B, Rev 01 (as drawn)</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Raised against the flowchart. Rev 02 answers the mismatch and refurbishment questions from the implementation; the other two are still open.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>{RMA_OPEN_QUESTIONS.map(questionCard)}</div>
        <p className="ni-note" style={{ marginBottom: 0, marginTop: 14 }}>Source: {RMA_DOC.source}</p>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>How the two flows are drawn</h2>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: 'var(--ni-text-soft)' }}>
          <li>
            <strong>One process, two systems.</strong> Flow A is the support loop and runs in {LOOPS.support.system};
            Flow B is the service loop and runs in {LOOPS.service.system}. The seam between them is drawn explicitly
            rather than left as a gap, because the handover is where cases historically went quiet.
          </li>
          <li>
            <strong>Flow B Rev 02 uses the system&rsquo;s own vocabulary.</strong> Block codes are the real status names and
            the real endpoints, so a reference can be grepped. Status blocks carry a STATUS badge and an accent bar;
            everything else is an action that moves between them.
          </li>
          <li>
            <strong>MTTR is a badge, not a box.</strong> The flowchart had two standalone marker nodes joined by a dotted
            link; in a vertical flow those render as orphans, so they became badges on the steps that actually set the
            timestamps. Rev 02 puts them where the code puts them, which is not where the drawing put them.
          </li>
          <li>
            <strong>Lane colours are shared across both flows</strong> so the eye can follow one actor from support into
            service. That means customer is blue, not the green the source flowchart used.
          </li>
        </ul>
      </section>
    </>
  );
}

/* ------------------------------------------------------------- ISO alignment */

function IsoView({ narrow }) {
  const sevTone = {
    high: { color: '#F09090', border: 'rgba(224,96,96,0.55)', bg: 'rgba(224,96,96,0.08)', label: 'Fix first' },
    medium: { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.07)', label: 'Should fix' },
    low: { color: 'var(--ni-text-muted)', border: 'var(--ni-border-soft)', bg: 'rgba(255,255,255,0.02)', label: 'Worth doing' },
  };

  const byStandard = useMemo(() => {
    const map = {};
    Object.values(NODES).forEach(n => {
      const iso = n.detail && n.detail.iso;
      if (!iso) return;
      iso.forEach(r => {
        const { std, clause } = parseRef(r.ref);
        if (!map[std]) map[std] = {};
        if (!map[std][clause]) map[std][clause] = { why: r.why, nodes: [] };
        map[std][clause].nodes.push(n);
      });
    });
    return map;
  }, []);

  return (
    <>
      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0 }}>Should ISO standards be built into this flow?</h2>
        <p className="ni-section-copy">
          Yes — but as an overlay, not a rewrite. The flow you drew is already close to what the standards ask for; the
          shape is right. What the standards add is a short list of steps that were missing, and a reason to keep doing
          the parts that feel like overhead. Nothing below changes the tier model or the four close states.
        </p>
        <div className="ni-callout" style={{ marginTop: 12 }}>
          <strong>The one-line argument for doing it:</strong> ISO 9001 already requires you to handle customer feedback
          and complaints, act on nonconformities, and keep the records. You are going to do this work regardless. Tagging
          the flow to the clauses means the same effort also produces audit evidence, instead of producing it twice.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginTop: 16 }}>
          {Object.entries(STANDARDS).map(([key, s]) => (
            <div key={key} style={{
              border: '1px solid rgba(169,139,219,0.3)', background: 'rgba(169,139,219,0.06)',
              borderRadius: 12, padding: '11px 14px',
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: '#C7B2EC' }}>{s.name}</div>
              <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--ni-text-muted)', marginTop: 3 }}>{s.scope}</div>
            </div>
          ))}
        </div>
        <p className="ni-note" style={{ marginBottom: 0, marginTop: 14 }}>
          ISO 10002 is the standard that matters most here — it is specifically about handling customer complaints, and
          its clause order is almost exactly this flow: communication → receipt → tracking → acknowledgement → initial
          assessment → investigation → response → communicating the decision → closing. ISO/IEC 20000-1 contributes the
          tiered incident model. Clause numbers follow the editions named above — check them against your controlled
          copies before quoting them in an audit.
        </p>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Gap analysis — what Rev 01/02 did not do</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Twelve findings, ordered by how much damage they do. The first three are real dead ends in the chart, not
          paperwork: cases could fall out of the process entirely. Each links to the Rev 03 node that closes it.
        </p>
        <div style={{ display: 'grid', gap: 11 }}>
          {ISO_GAPS.map(g => {
            const t = sevTone[g.severity];
            return (
              <div key={g.id} style={{ border: `1px solid ${t.border}`, background: t.bg, borderRadius: 14, padding: '13px 16px' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: t.color, border: `1px solid ${t.border}`, borderRadius: 999, padding: '2px 9px',
                  }}>{t.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{g.gap}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '8px 20px', marginTop: 8 }}>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 3 }}>What happens today</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{g.found}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-green)', marginBottom: 3 }}>Proposed in Rev 03</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{g.fix}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                  {g.iso.map(r => (
                    <span key={r} style={{
                      fontSize: 9.5, fontWeight: 700, color: '#C7B2EC', background: 'rgba(169,139,219,0.13)',
                      border: '1px solid rgba(169,139,219,0.32)', borderRadius: 4, padding: '1px 6px',
                    }}>{refLabel(r)}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Clause → step coverage</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Which step in the flow satisfies which clause. This is the table an auditor asks for.
        </p>
        {Object.entries(byStandard).map(([std, clauses]) => (
          <div key={std} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C7B2EC', marginBottom: 8 }}>
              {STANDARDS[std] ? STANDARDS[std].name : std}
            </div>
            <div className="ni-table-wrap">
              <table className="ni-table">
                <thead>
                  <tr><th style={{ width: 120 }}>Clause</th><th>Steps that satisfy it</th></tr>
                </thead>
                <tbody>
                  {Object.entries(clauses).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })).map(([clause, info]) => (
                    <tr key={clause}>
                      <td style={{ fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{clause || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 4 }}>
                          {info.nodes.map(n => (
                            <span key={n.id} style={{
                              fontSize: 10.5, fontWeight: 700, color: LANES[n.lane].code, background: LANES[n.lane].bg,
                              border: `1px solid ${LANES[n.lane].border}`, borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap',
                            }}>
                              {n.code} {n.title}{n.proposed ? ' ·' : ''}
                            </span>
                          ))}
                        </div>
                        <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--ni-text-muted)' }}>{info.why}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        <p className="ni-note" style={{ marginBottom: 0, marginTop: 14 }}>
          A trailing · marks a step that only exists in the Rev 03 proposal — that clause is not covered until Rev 03 is accepted.
        </p>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>What we deliberately did not add</h2>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: 'var(--ni-text-soft)' }}>
          <li><strong>No formal complaints committee or dispute-resolution stage.</strong> ISO 10002 discusses external recourse. At your volume that is ceremony — the escalation owner plus the quality manager is the right weight.</li>
          <li><strong>No separate incident/problem/change split.</strong> ISO/IEC 20000-1 separates these. Your #5.4/#5.5 already routes systemic findings into NCR/ECO, which is the same idea using tools you already have.</li>
          <li><strong>No ISO 13485 content.</strong> That is a medical-device QMS standard and does not apply to this product line.</li>
          <li><strong>No new document hierarchy.</strong> This flow should be a controlled document inside the existing QMS rather than a parallel system — which is also why it carries a revision, an owner and an approver.</li>
        </ul>
      </section>
    </>
  );
}

/* ---------------------------------------------------------- revision history */

function RevisionsView() {
  return (
    <>
      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Document control</h2>
        <div className="ni-table-wrap">
          <table className="ni-table">
            <tbody>
              <tr><td style={{ width: 210, color: 'var(--ni-text-muted)' }}>Flow A</td><td style={{ fontWeight: 700 }}>{DOC.id} — {DOC.title}</td></tr>
              <tr><td style={{ color: 'var(--ni-text-muted)' }}>Current revision</td><td>{REVISIONS[REVISIONS.length - 1].label} · {REVISIONS[REVISIONS.length - 1].status}</td></tr>
              <tr><td style={{ color: 'var(--ni-text-muted)' }}>Flow B</td><td style={{ fontWeight: 700 }}>{RMA_DOC.id} — {RMA_DOC.title}</td></tr>
              <tr><td style={{ color: 'var(--ni-text-muted)' }}>Current revision</td><td>{RMA_DOC.revision} · {RMA_DOC.status} · {RMA_DOC.date}</td></tr>
              <tr><td style={{ color: 'var(--ni-text-muted)' }}>Process owner</td><td>{DOC.processOwner}</td></tr>
              <tr><td style={{ color: 'var(--ni-text-muted)' }}>System / tool owner</td><td>{DOC.systemOwner}</td></tr>
              <tr><td style={{ color: 'var(--ni-text-muted)' }}>Approver</td><td>{DOC.approver}</td></tr>
            </tbody>
          </table>
        </div>
        <div style={{
          marginTop: 14, border: '1px dashed rgba(217,180,74,0.55)', background: 'rgba(217,180,74,0.06)',
          borderRadius: 12, padding: '11px 14px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)',
        }}>
          <strong style={{ color: '#E8CD7E' }}>⚠ Owners are unassigned.</strong> Three names are needed before this can be
          issued: who owns the process, who owns the system, and who approves changes. ISO 9001 5.3 asks for exactly this,
          and in practice an unowned process is the reason rollouts quietly stop.
        </div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Revision history — Flow A</h2>
        <p className="ni-section-copy" style={{ marginTop: 0 }}>
          Switch revision in the bar above to see the diagram as it stood at each one. Flow B is at {RMA_DOC.revision} and has no earlier revisions.
        </p>
        <div style={{ display: 'grid', gap: 14 }}>
          {[...REVISIONS].reverse().map(r => (
            <div key={r.rev} style={{ border: '1px solid var(--ni-border-soft)', borderRadius: 14, padding: '13px 16px', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--ni-green)' }}>{r.label}</span>
                <span style={{ fontSize: 12, color: 'var(--ni-text-muted)' }}>{r.date}</span>
                <span style={{
                  fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: 'var(--ni-text-soft)', border: '1px solid var(--ni-border-soft)', borderRadius: 999, padding: '2px 9px',
                }}>{r.status}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ni-text-muted)', marginLeft: 'auto' }}>{r.author}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 7 }}>{r.summary}</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>
                {r.changes.map((c, i) => <li key={i} style={{ marginBottom: 2 }}>{c}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="ni-panel">
        <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>How to raise the next revision</h2>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.75, color: 'var(--ni-text-soft)' }}>
          <li>Anyone can propose a change — the periodic support review (#7.2) is the standing forum for it.</li>
          <li>The process owner writes the change as a new entry in <code style={{ fontSize: 12 }}>supportFlowData.js</code>: a new revision with a summary and an itemised change list, and new nodes tagged <code style={{ fontSize: 12 }}>since</code> the new revision.</li>
          <li>Anything not yet agreed is marked <code style={{ fontSize: 12 }}>proposed</code>, so it renders with a PROPOSED badge and never silently replaces the approved flow.</li>
          <li>The approver reviews the diagram at the new revision, then the status moves from <em>Draft</em> to <em>Issued</em> and the previous revision becomes <em>Superseded</em>.</li>
          <li>Every revision stays viewable here. Nothing is deleted — which is what makes this usable as evidence under ISO 9001 7.5.3.</li>
        </ol>
        <p className="ni-note" style={{ marginBottom: 0, marginTop: 12 }}>
          Revisions are held in the repository, so the git history is the audit trail for who changed what and when.
          If you want revisions to be created in the browser instead, that needs a backing model and endpoint — say so and it can be added.
        </p>
      </section>
    </>
  );
}
